import {
    getSkillEditorPublishedFilenameComparisonKey,
    getSkillEditorPublishedFilenameStem,
} from "./skill-editor-filenames";
import { type SkillEditorReferences } from "./skill-editor-references";
import {
    deriveSkillEditorDiff,
    getSkillEditorScopeAssignment,
    isSkillEditorNameNormalizationRepair,
    normalizeSkillEditorText,
    type SkillEditorDiff,
    type SkillEditorFile,
    type SkillEditorFileField,
    type SkillEditorSource,
    type SkillEditorSourceFile,
    type SkillEditorState,
    type SkillEditorSkillField,
} from "./skill-editor-state";

export {
    deriveSkillEditorDiff,
    isSkillEditorDirty,
    isSkillEditorFileDirty,
} from "./skill-editor-state";
export type {
    SkillEditorDiff,
    SkillEditorFileField,
    SkillEditorSkillField,
    SkillEditorUpdatedFileDiff,
} from "./skill-editor-state";
export {
    getSkillEditorPublishedFilename,
    getSkillEditorPublishedFilenameComparisonKey,
} from "./skill-editor-filenames";

export interface SkillEditorSkillUpdatePayload {
    readonly name: string;
    readonly description: string;
    readonly content: string;
    readonly organization_id: number | null;
    readonly organization_group_id: number | null;
    readonly is_core: boolean;
}

export interface SkillEditorFilePayload {
    readonly name: string;
    readonly content: string;
}

export type SkillEditorSaveIntent = "draft" | "publish";

export interface SkillEditorPublicationToken {
    readonly claudeSkillId: string | null;
    readonly publishedAt: string | null;
}

export type SkillEditorSaveOperation =
    | {
          readonly kind: "update-skill";
          readonly skillId: number;
          readonly payload: SkillEditorSkillUpdatePayload;
      }
    | {
          readonly kind: "delete-file";
          readonly skillId: number;
          readonly fileId: number;
      }
    | {
          readonly kind: "update-file";
          readonly skillId: number;
          readonly clientKey: string;
          readonly fileId: number;
          readonly payload: SkillEditorFilePayload;
      }
    | {
          readonly kind: "add-file";
          readonly skillId: number;
          readonly clientKey: string;
          readonly payload: SkillEditorFilePayload;
      }
    | {
          readonly kind: "snapshot";
          readonly skillId: number;
      }
    | {
          readonly kind: "publish";
          readonly skillId: number;
      };

export interface SkillEditorPublishAttempt {
    readonly operation: Extract<SkillEditorSaveOperation, { kind: "publish" }>;
    readonly publicationToken: SkillEditorPublicationToken;
}

export type SkillEditorOperationReceipt =
    | {
          readonly kind: "update-skill";
          readonly operation: Extract<
              SkillEditorSaveOperation,
              { kind: "update-skill" }
          >;
          readonly skill: SkillEditorSource;
      }
    | {
          readonly kind: "delete-file";
          readonly operation: Extract<
              SkillEditorSaveOperation,
              { kind: "delete-file" }
          >;
          readonly outcome: "deleted" | "already-missing";
      }
    | {
          readonly kind: "update-file";
          readonly operation: Extract<
              SkillEditorSaveOperation,
              { kind: "update-file" }
          >;
          readonly file: SkillEditorSourceFile;
      }
    | {
          readonly kind: "add-file";
          readonly operation: Extract<
              SkillEditorSaveOperation,
              { kind: "add-file" }
          >;
          readonly file: SkillEditorSourceFile;
      }
    | {
          readonly kind: "snapshot";
          readonly operation: Extract<
              SkillEditorSaveOperation,
              { kind: "snapshot" }
          >;
      }
    | {
          readonly kind: "publish";
          readonly operation: Extract<
              SkillEditorSaveOperation,
              { kind: "publish" }
          >;
          readonly skill: SkillEditorSource;
      };

export interface SkillEditorSavePlan {
    readonly intent: SkillEditorSaveIntent;
    readonly capturedGeneration: number;
    readonly hasVersionedChanges: boolean;
    readonly publicationToken: SkillEditorPublicationToken;
    readonly nameCheck: SkillEditorNameCheck;
    readonly diff: SkillEditorDiff;
    readonly operations: readonly SkillEditorSaveOperation[];
}

export interface SkillEditorNameCheck {
    readonly name: string;
    readonly excludeId: number;
}

export interface SkillEditorConflictFenceFile {
    readonly serverId: number;
    readonly fileName: string;
    readonly updatedAt: string;
}

export interface SkillEditorConflictFence {
    readonly token: string;
    readonly skillUpdatedAt: string;
    readonly files: readonly SkillEditorConflictFenceFile[];
}

export type SkillEditorConflictValue = string | number | boolean | null;

export interface SkillEditorSkillFieldConflict {
    readonly scope: "skill";
    readonly field: SkillEditorSkillField;
    readonly fieldLabel: string;
    readonly remoteValue: SkillEditorConflictValue;
}

export interface SkillEditorFileFieldConflict {
    readonly scope: "file";
    readonly clientKey: string;
    readonly serverId: number;
    readonly fileName: string;
    readonly field: SkillEditorFileField;
    readonly fieldLabel: string;
    readonly remoteValue: string;
    readonly remoteUpdatedAt: string;
}

export type SkillEditorFieldConflict =
    | SkillEditorSkillFieldConflict
    | SkillEditorFileFieldConflict;

export interface SkillEditorRemoteEditAgainstLocalDelete {
    readonly clientKey: string;
    readonly serverId: number;
    readonly fileName: string;
    readonly remoteUpdatedAt: string;
}

export interface SkillEditorLocalEditAgainstRemoteDelete {
    readonly clientKey: string;
    readonly serverId: number;
    readonly fileName: string;
}

export interface SkillEditorFilenameCollision {
    readonly localClientKey: string;
    readonly localFileName: string;
    readonly remoteServerId: number;
    readonly remoteFileName: string;
    readonly remoteUpdatedAt: string;
}

export type SkillEditorConflictCase =
    | {
          readonly kind: "overlapping-edits";
          readonly fields: readonly SkillEditorFieldConflict[];
      }
    | {
          readonly kind: "remote-edit-against-local-delete";
          readonly files: readonly SkillEditorRemoteEditAgainstLocalDelete[];
      }
    | {
          readonly kind: "local-edit-against-remote-delete";
          readonly files: readonly SkillEditorLocalEditAgainstRemoteDelete[];
      }
    | {
          readonly kind: "concurrent-filename-or-path-collision";
          readonly collisions: readonly SkillEditorFilenameCollision[];
      };

export interface SkillEditorSaveConflict {
    readonly fence: SkillEditorConflictFence;
    readonly cases: readonly SkillEditorConflictCase[];
    readonly authoritative: SkillEditorSource;
    readonly capturedState: SkillEditorState;
}

export type SkillEditorSavePreflight =
    | {
          readonly kind: "ready";
          readonly state: SkillEditorState;
          readonly plan: SkillEditorSavePlan;
          readonly fence: SkillEditorConflictFence;
      }
    | {
          readonly kind: "conflict";
          readonly conflict: SkillEditorSaveConflict;
      }
    | {
          readonly kind: "invalid";
          readonly state: SkillEditorState;
          readonly validation: SkillEditorValidation;
          readonly fence: SkillEditorConflictFence;
      };

export interface SkillEditorSaveRecoveryBase {
    readonly completedOperations: readonly SkillEditorOperationReceipt[];
}

export type SkillEditorSaveReconcileRecovery =
    | {
          readonly kind: "idless-local";
          readonly clientKey: string;
          readonly fileName: string;
      }
    | {
          readonly kind: "idless-local-server-collision";
          readonly clientKey: string;
          readonly localFileName: string;
          readonly remoteServerId: number;
          readonly remoteFileName: string;
          readonly remoteUpdatedAt: string;
          readonly publishedPath: string;
      }
    | {
          readonly kind: "server-file-collision";
          readonly clientKey: string;
          readonly localServerId: number;
          readonly localFileName: string;
          readonly remoteServerId: number;
          readonly remoteFileName: string;
          readonly remoteUpdatedAt: string;
          readonly publishedPath: string;
      };

export type SkillEditorPublishStatusUnknownRecovery =
    SkillEditorSaveRecoveryBase & {
        readonly kind: "publish-status-unknown";
        readonly attempt: SkillEditorPublishAttempt;
        readonly error: unknown;
        readonly observedPublicationToken: SkillEditorPublicationToken | null;
    };

export type SkillEditorDraftSavedPublishRejectedRecovery =
    SkillEditorSaveRecoveryBase & {
        readonly kind: "draft-saved-publish-rejected";
        readonly attempt: SkillEditorPublishAttempt;
        readonly error: unknown;
    };

export type SkillEditorPublishUnpublishedElsewhereRecovery =
    SkillEditorSaveRecoveryBase & {
        readonly kind: "publish-unpublished-elsewhere";
        readonly attempt: SkillEditorPublishAttempt;
        readonly error: unknown;
        readonly observedPublicationToken: SkillEditorPublicationToken;
    };

export type SkillEditorSaveRecovery =
    | (SkillEditorSaveRecoveryBase & {
          readonly kind: "retry-blocked";
          readonly error: unknown;
      })
    | SkillEditorPublishStatusUnknownRecovery
    | SkillEditorDraftSavedPublishRejectedRecovery
    | SkillEditorPublishUnpublishedElsewhereRecovery
    | (SkillEditorSaveRecoveryBase & SkillEditorSaveReconcileRecovery);

export type SkillEditorSaveStatus =
    | { readonly kind: "idle" }
    | { readonly kind: "saved" }
    | { readonly kind: "saved-with-refresh-warning"; readonly error: unknown };

export interface SkillEditorSaveReconcileResult {
    readonly state: SkillEditorState;
    readonly recovery: SkillEditorSaveReconcileRecovery | null;
}

export type SkillEditorValidationCode =
    | "organization-required"
    | "organization-group-required"
    | "file-name-required"
    | "file-name-too-long"
    | "file-name-forbidden-character"
    | "file-content-required"
    | "file-name-duplicate"
    | "published-stem-empty"
    | "published-path-reserved"
    | "published-path-duplicate"
    | "ordering-dependent-rename"
    | "reference-name-reuse"
    | "rename-acknowledgment-required";

export interface SkillEditorValidationIssue {
    readonly clientKey: string;
    readonly field: "name" | "content" | "rename";
    readonly code: SkillEditorValidationCode;
    readonly message: string;
}

export interface SkillEditorValidation {
    readonly isValid: boolean;
    readonly issues: readonly SkillEditorValidationIssue[];
}

export class SkillEditorSaveValidationError extends Error {
    readonly validation: SkillEditorValidation;

    constructor(validation: SkillEditorValidation) {
        super("Skill editor validation failed");
        this.name = "SkillEditorSaveValidationError";
        this.validation = validation;
    }
}

const hasForbiddenFilenameCharacter = (name: string): boolean =>
    [...name].some((character) => {
        const codePoint = character.codePointAt(0) ?? 0;
        return (
            character === "/" ||
            character === "\\" ||
            codePoint <= 31 ||
            (codePoint >= 127 && codePoint <= 159)
        );
    });

export interface SkillEditorFilenameValidationIssue {
    readonly code:
        | "file-name-required"
        | "file-name-too-long"
        | "file-name-forbidden-character"
        | "published-stem-empty"
        | "published-path-reserved";
    readonly message: string;
}

export function getSkillEditorFilenameValidationIssues(
    value: string
): readonly SkillEditorFilenameValidationIssue[] {
    const name = normalizeSkillEditorText(value);
    if (name.length === 0) {
        return [
            {
                code: "file-name-required",
                message: "Give this reference a name",
            },
        ];
    }

    const issues: SkillEditorFilenameValidationIssue[] = [];
    if (name.length > 255) {
        issues.push({
            code: "file-name-too-long",
            message: "Reference names must be 255 characters or fewer",
        });
    }
    if (hasForbiddenFilenameCharacter(name)) {
        issues.push({
            code: "file-name-forbidden-character",
            message:
                "Reference names cannot contain slashes or control characters",
        });
    }
    if (getSkillEditorPublishedFilenameStem(name).length === 0) {
        issues.push({
            code: "published-stem-empty",
            message:
                "Reference names must produce a non-empty published filename",
        });
    }
    if (getSkillEditorPublishedFilenameComparisonKey(name) === "skill.md") {
        issues.push({
            code: "published-path-reserved",
            message:
                "Reference names cannot publish to the reserved SKILL.md filename",
        });
    }
    return issues;
}

export function validateSkillEditorState(
    state: SkillEditorState,
    references: SkillEditorReferences
): SkillEditorValidation {
    const issues: SkillEditorValidationIssue[] = [];
    const authoredFiles = state.buffer.files.filter(
        ({ isPlaceholder }) => !isPlaceholder
    );
    const addIssue = (
        clientKey: string,
        field: SkillEditorValidationIssue["field"],
        code: SkillEditorValidationCode,
        message: string
    ) => {
        if (
            !issues.some(
                (issue) => issue.clientKey === clientKey && issue.code === code
            )
        ) {
            issues.push({ clientKey, field, code, message });
        }
    };

    if (
        state.buffer.skill.scopeKind === "organization" &&
        state.buffer.skill.organizationId === null
    ) {
        addIssue(
            "settings",
            "name",
            "organization-required",
            "Choose an organization"
        );
    }
    if (
        state.buffer.skill.scopeKind === "group" &&
        state.buffer.skill.organizationGroupId === null
    ) {
        addIssue(
            "settings",
            "name",
            "organization-group-required",
            "Choose a group"
        );
    }

    for (const file of authoredFiles) {
        const name = normalizeSkillEditorText(file.name);
        for (const issue of getSkillEditorFilenameValidationIssues(name)) {
            addIssue(file.clientKey, "name", issue.code, issue.message);
        }
        if (normalizeSkillEditorText(file.content).length === 0) {
            addIssue(
                file.clientKey,
                "content",
                "file-content-required",
                "Add content to this file"
            );
        }
    }

    const filesByLogicalName = new Map<string, SkillEditorFile[]>();
    const filesByPublishedPath = new Map<string, SkillEditorFile[]>();
    for (const file of authoredFiles) {
        const name = normalizeSkillEditorText(file.name);
        if (name.length === 0) continue;
        const logicalName = name.toLowerCase();
        filesByLogicalName.set(logicalName, [
            ...(filesByLogicalName.get(logicalName) ?? []),
            file,
        ]);
        const publishedPath =
            getSkillEditorPublishedFilenameComparisonKey(name);
        filesByPublishedPath.set(publishedPath, [
            ...(filesByPublishedPath.get(publishedPath) ?? []),
            file,
        ]);
    }
    for (const files of filesByLogicalName.values()) {
        if (files.length < 2) continue;
        for (const file of files) {
            addIssue(
                file.clientKey,
                "name",
                "file-name-duplicate",
                "Reference names must be unique"
            );
        }
    }
    for (const files of filesByPublishedPath.values()) {
        if (files.length < 2) continue;
        for (const file of files) {
            addIssue(
                file.clientKey,
                "name",
                "published-path-duplicate",
                "Reference names must publish to unique filenames"
            );
        }
    }

    const diff = deriveSkillEditorDiff(state);
    for (const { file } of diff.orderingDependentRenames) {
        addIssue(
            file.clientKey,
            "rename",
            "ordering-dependent-rename",
            "Use a unique intermediate name before reusing an existing reference name"
        );
    }

    for (const { file, baseline, changedFields } of diff.updatedFiles) {
        if (!changedFields.includes("name")) continue;

        if (
            !isSkillEditorNameNormalizationRepair(file.name, baseline.name) &&
            file.acknowledgedRename !== file.name
        ) {
            addIssue(
                file.clientKey,
                "rename",
                "rename-acknowledgment-required",
                "Commit this reference rename before saving"
            );
        }

        const origin = normalizeSkillEditorText(
            file.renameOrigin ?? baseline.name
        );
        const originIsReused = authoredFiles.some(
            (candidate) =>
                candidate.clientKey !== file.clientKey &&
                normalizeSkillEditorText(candidate.name) === origin
        );
        if (
            originIsReused &&
            references.occurrences.some(
                (occurrence) => occurrence.typedTarget === origin
            )
        ) {
            for (const candidate of authoredFiles) {
                if (
                    candidate.clientKey !== file.clientKey &&
                    normalizeSkillEditorText(candidate.name) === origin
                ) {
                    addIssue(
                        candidate.clientKey,
                        "name",
                        "reference-name-reuse",
                        "Repair references to the renamed file before reusing its original name"
                    );
                }
            }
        }
    }

    return { isValid: issues.length === 0, issues };
}

const captureDiff = (diff: SkillEditorDiff): SkillEditorDiff => ({
    skillFields: [...diff.skillFields],
    deletedFiles: diff.deletedFiles.map((file) => ({ ...file })),
    updatedFiles: diff.updatedFiles.map(
        ({ file, baseline, changedFields }) => ({
            file: { ...file },
            baseline: { ...baseline },
            changedFields: [...changedFields],
        })
    ),
    addedFiles: diff.addedFiles.map((file) => ({ ...file })),
    orderingDependentRenames: diff.orderingDependentRenames.map(
        ({ file, baseline, changedFields }) => ({
            file: { ...file },
            baseline: { ...baseline },
            changedFields: [...changedFields],
        })
    ),
    hasPersistedChanges: diff.hasPersistedChanges,
    hasVersionedChanges: diff.hasVersionedChanges,
});

export function planSkillEditorSave(
    state: SkillEditorState,
    intent: SkillEditorSaveIntent,
    references: SkillEditorReferences
): SkillEditorSavePlan {
    const validation = validateSkillEditorState(state, references);
    if (!validation.isValid) {
        throw new SkillEditorSaveValidationError(validation);
    }
    const diff = deriveSkillEditorDiff(state);
    const operations: SkillEditorSaveOperation[] = [];

    if (diff.skillFields.length > 0) {
        const scope = getSkillEditorScopeAssignment(state.buffer.skill);
        operations.push({
            kind: "update-skill",
            skillId: state.skillId,
            payload: {
                name: normalizeSkillEditorText(state.buffer.skill.name),
                description: normalizeSkillEditorText(
                    state.buffer.skill.description
                ),
                content: state.buffer.skill.content,
                organization_id: scope.organizationId,
                organization_group_id: scope.organizationGroupId,
                is_core: state.buffer.skill.isCore,
            },
        });
    }

    for (const file of diff.deletedFiles) {
        operations.push({
            kind: "delete-file",
            skillId: state.skillId,
            fileId: file.serverId,
        });
    }

    for (const { file, baseline } of diff.updatedFiles) {
        operations.push({
            kind: "update-file",
            skillId: state.skillId,
            clientKey: file.clientKey,
            fileId: baseline.serverId,
            payload: {
                name: normalizeSkillEditorText(file.name),
                content: file.content,
            },
        });
    }

    for (const file of diff.addedFiles) {
        operations.push({
            kind: "add-file",
            skillId: state.skillId,
            clientKey: file.clientKey,
            payload: {
                name: normalizeSkillEditorText(file.name),
                content: file.content,
            },
        });
    }

    if (diff.hasVersionedChanges) {
        operations.push({ kind: "snapshot", skillId: state.skillId });
    }
    if (intent === "publish") {
        operations.push({ kind: "publish", skillId: state.skillId });
    }

    return {
        intent,
        capturedGeneration: state.editGeneration,
        hasVersionedChanges: diff.hasVersionedChanges,
        publicationToken: {
            claudeSkillId: state.baseline.skill.claudeSkillId,
            publishedAt: state.baseline.skill.publishedAt,
        },
        nameCheck: {
            name: normalizeSkillEditorText(state.buffer.skill.name),
            excludeId: state.skillId,
        },
        diff: captureDiff(diff),
        operations,
    };
}

export function matchesSkillEditorNameCheck(
    state: SkillEditorState,
    nameCheck: SkillEditorNameCheck
): boolean {
    return (
        state.skillId === nameCheck.excludeId &&
        normalizeSkillEditorText(state.buffer.skill.name) === nameCheck.name
    );
}
